import { HelpCircle, X } from 'lucide-react';
import { useState } from 'react';

// Purpose: Reusable help support modal content
const HelpSupportModal = ({ onClose }: { onClose: () => void }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Need Help?</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 text-gray-600">
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Contact Support</h3>
            <p>
              Call or text us at{' '}
              <a href="tel:+19492827201" className="text-blue-600 hover:text-blue-700">
                (949) 282-7201
              </a>
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Hours</h3>
            <p>Monday - Friday: 9:00 AM - 5:00 PM PST</p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
            <a
              href="mailto:dylan@bigtunasolutions.com"
              className="text-blue-600 hover:text-blue-700"
            >
              dylan@bigtunasolutions.com
            </a>
          </div>

          <div className="text-sm text-gray-500 mt-6">
            <p>
              For urgent matters outside of business hours, please call us at (949) 282-7201 and we
              will assist you as soon as possible.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface HelpButtonProps {
  variant?: 'fixed' | 'inline';
}

// Purpose: Button component that triggers the help support modal
export const HelpButton = ({ variant = 'fixed' }: HelpButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const buttonClassName =
    variant === 'fixed'
      ? 'fixed bottom-6 right-6 p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-colors z-50'
      : 'inline-flex flex-shrink-0 mx-auto p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-colors w-12 h-12 items-center justify-center';

  return (
    <>
      <button onClick={() => setIsModalOpen(true)} className={buttonClassName} aria-label="Help">
        <HelpCircle className="h-6 w-6" />
      </button>

      {isModalOpen && <HelpSupportModal onClose={() => setIsModalOpen(false)} />}
    </>
  );
};

// Purpose: Standalone modal that can be triggered from anywhere (like sidebar nav)
export const useHelpSupport = () => {
  const [isOpen, setIsOpen] = useState(false);

  const openHelpSupport = () => setIsOpen(true);
  const closeHelpSupport = () => setIsOpen(false);

  const HelpSupportModalComponent = isOpen ? <HelpSupportModal onClose={closeHelpSupport} /> : null;

  return {
    openHelpSupport,
    closeHelpSupport,
    HelpSupportModalComponent,
  };
};
